import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
    DropdownMenuPortal,
} from './ui/dropdown-menu';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from './ui/collapsible';
import { ScrollArea } from './ui/scroll-area';
import {
    LayoutDashboard,
    Building2,
    Users,
    Kanban,
    FileText,
    Receipt,
    Car,
    Settings,
    LogOut,
    Search,
    Bell,
    Sun,
    Moon,
    Menu,
    X,
    ChevronDown,
    ChevronRight,
    UserCog,
    BookOpen,
    Download,
    Calculator,
    Mail,
    HardDrive,
    ExternalLink,
    RefreshCw,
    Calendar as CalendarIcon,
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_bda23fca-8d07-481c-a0f9-69b9b81c1bdb/artifacts/a69z698w_image2.png';

// Magazines configuration
const MAGAZINES = [
    { id: 'decideur', label: 'Décideur' },
    { id: 'theplacetob', label: 'The Place to B' },
    { id: 'magimmo', label: "Mag' de l'Immo" },
];

const Layout = ({ children }) => {
    const { user, logout, isAdmin, isSuperAdmin, api } = useAuth();
    const { theme, toggleTheme, isDark } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [productionOpen, setProductionOpen] = useState(false);
    const [magazines, setMagazines] = useState([]);

    useEffect(() => {
        // Fetch magazines from API
        const fetchMagazines = async () => {
            try {
                const response = await api.get('/magazines');
                setMagazines(response.data || []);
            } catch (error) {
                console.error('Failed to fetch magazines:', error);
            }
        };
        if (user) {
            fetchMagazines();
        }
    }, [user, api]);

    const menuItems = [
        { path: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord', roles: ['commercial', 'assistant', 'editorial', 'super_admin'] },
        { path: '/companies', icon: Building2, label: 'Entreprises clientes', roles: ['commercial', 'assistant', 'super_admin'] },
        { path: '/contacts', icon: Users, label: 'Contacts', roles: ['commercial', 'assistant', 'super_admin'] },
        { path: '/pipeline', icon: Kanban, label: 'Pipeline', roles: ['commercial', 'assistant', 'super_admin'] },
        { path: '/quote-requests', icon: FileText, label: 'Demandes de devis', roles: ['commercial', 'assistant', 'super_admin'] },
        { path: '/calendar', icon: CalendarIcon, label: 'Calendrier', roles: ['commercial', 'assistant', 'editorial', 'super_admin'] },
        { path: '/expenses', icon: Receipt, label: 'Notes de frais', roles: ['commercial', 'assistant', 'super_admin'] },
        { path: '/commissions', icon: Calculator, label: 'Commissions', roles: ['commercial', 'assistant', 'super_admin'] },
        { path: '/vehicles', icon: Car, label: 'Véhicules', roles: ['commercial', 'assistant', 'super_admin'] },
        { path: '/exports', icon: Download, label: 'Exports', roles: ['assistant', 'super_admin'] },
    ];

    // Production menu is special - has submenu
    const hasProductionAccess = ['editorial', 'assistant', 'super_admin'].includes(user?.role);

    const adminMenuItems = [
        { path: '/admin/users', icon: UserCog, label: 'Utilisateurs', roles: ['super_admin'] },
        { path: '/admin/files', icon: HardDrive, label: 'Fichiers', roles: ['super_admin'] },
        { path: '/admin/settings', icon: Settings, label: 'Paramètres', roles: ['super_admin'] },
    ];

    const filteredMenuItems = menuItems.filter(item => item.roles.includes(user?.role));
    const filteredAdminItems = adminMenuItems.filter(item => item.roles.includes(user?.role));

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const getInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    const getRoleLabel = (role) => {
        const labels = {
            commercial: 'Commercial',
            assistant: 'Assistant',
            editorial: 'Éditorial',
            super_admin: 'Super Admin',
        };
        return labels[role] || role;
    };

    const isProductionActive = location.pathname.startsWith('/editorial') || location.pathname.startsWith('/production');

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-64 sidebar border-r border-border/40 transform transition-transform duration-300 lg:translate-x-0 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="flex items-center justify-center h-16 px-4 border-b border-white/10">
                        <img src={LOGO_URL} alt="Place des Médias" className="h-10 w-auto" />
                    </div>

                    {/* Navigation */}
                    <ScrollArea className="flex-1 py-6">
                        <nav className="px-4 space-y-1">
                            {filteredMenuItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.path;
                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        data-testid={`nav-${item.path.replace('/', '')}`}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                                            isActive
                                                ? 'bg-primary text-white'
                                                : 'text-white/70 hover:bg-white/10 hover:text-white'
                                        }`}
                                        onClick={() => setSidebarOpen(false)}
                                    >
                                        <Icon className="w-5 h-5" />
                                        {item.label}
                                    </Link>
                                );
                            })}

                            {/* Production Menu with Submenu */}
                            {hasProductionAccess && (
                                <Collapsible open={productionOpen || isProductionActive} onOpenChange={setProductionOpen}>
                                    <CollapsibleTrigger asChild>
                                        <button
                                            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                                                isProductionActive
                                                    ? 'bg-primary text-white'
                                                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                                            }`}
                                            data-testid="nav-production"
                                        >
                                            <div className="flex items-center gap-3">
                                                <BookOpen className="w-5 h-5" />
                                                Production
                                            </div>
                                            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${productionOpen || isProductionActive ? 'rotate-180' : ''}`} />
                                        </button>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="pl-4 mt-1 space-y-1">
                                        {magazines.length > 0 ? magazines.map((mag) => (
                                            <Link
                                                key={mag.id}
                                                to={`/editorial?magazine=${mag.id}`}
                                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                                                    location.search.includes(mag.id)
                                                        ? 'bg-white/20 text-white'
                                                        : 'text-white/60 hover:bg-white/10 hover:text-white'
                                                }`}
                                                onClick={() => setSidebarOpen(false)}
                                            >
                                                <ChevronRight className="w-4 h-4" />
                                                {mag.label}
                                            </Link>
                                        )) : MAGAZINES.map((mag) => (
                                            <Link
                                                key={mag.id}
                                                to={`/editorial?magazine=${mag.id}`}
                                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                                                    location.search.includes(mag.id)
                                                        ? 'bg-white/20 text-white'
                                                        : 'text-white/60 hover:bg-white/10 hover:text-white'
                                                }`}
                                                onClick={() => setSidebarOpen(false)}
                                            >
                                                <ChevronRight className="w-4 h-4" />
                                                {mag.label}
                                            </Link>
                                        ))}
                                        <Link
                                            to="/editorial"
                                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                                                location.pathname === '/editorial' && !location.search
                                                    ? 'bg-white/20 text-white'
                                                    : 'text-white/60 hover:bg-white/10 hover:text-white'
                                            }`}
                                            onClick={() => setSidebarOpen(false)}
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                            Tous les dossiers
                                        </Link>
                                    </CollapsibleContent>
                                </Collapsible>
                            )}

                            {filteredAdminItems.length > 0 && (
                                <>
                                    <div className="pt-6 pb-2 px-4">
                                        <span className="text-[10px] font-semibold text-white/40 uppercase tracking-[0.15em]">
                                            Administration
                                        </span>
                                    </div>
                                    {filteredAdminItems.map((item) => {
                                        const Icon = item.icon;
                                        const isActive = location.pathname === item.path;
                                        return (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                                                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                                                    isActive
                                                        ? 'bg-primary text-white'
                                                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                                                }`}
                                                onClick={() => setSidebarOpen(false)}
                                            >
                                                <Icon className="w-5 h-5" />
                                                {item.label}
                                            </Link>
                                        );
                                    })}
                                </>
                            )}
                        </nav>
                    </ScrollArea>

                    {/* User section */}
                    <div className="p-5 border-t border-white/10">
                        <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 bg-primary">
                                <AvatarFallback className="bg-primary text-white text-sm">
                                    {getInitials(user?.first_name, user?.last_name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">
                                    {user?.first_name} {user?.last_name}
                                </p>
                                <p className="text-xs text-white/50 truncate">
                                    {getRoleLabel(user?.role)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main content */}
            <div className="lg:pl-64">
                {/* Header */}
                <header className="sticky top-0 z-30 glass-header">
                    <div className="flex items-center justify-between h-16 px-4 lg:px-8">
                        {/* Mobile menu button */}
                        <button
                            data-testid="mobile-menu-btn"
                            className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-muted"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                        >
                            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>

                        {/* Spacer */}
                        <div className="flex-1" />

                        {/* Right actions */}
                        <div className="flex items-center gap-1">
                            {/* Gmail button */}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="rounded-full"
                                asChild
                                title="Gmail"
                            >
                                <a href="https://mail.google.com/mail/u/0/#inbox" target="_blank" rel="noopener noreferrer">
                                    <Mail className="w-5 h-5" />
                                </a>
                            </Button>

                            {/* Google Drive button */}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="rounded-full"
                                asChild
                                title="Google Drive"
                            >
                                <a href="https://drive.google.com/drive/u/0/shared-drives" target="_blank" rel="noopener noreferrer">
                                    <HardDrive className="w-5 h-5" />
                                </a>
                            </Button>

                            {/* Theme toggle */}
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={toggleTheme}
                                className="rounded-full"
                                data-testid="theme-toggle-btn"
                            >
                                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                            </Button>

                            {/* Refresh button */}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="rounded-full hover:bg-muted transition-colors"
                                onClick={() => window.location.reload()}
                                title="Rafraîchir la page"
                                data-testid="refresh-btn"
                            >
                                <RefreshCw className="w-5 h-5" />
                            </Button>

                            {/* Notifications */}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="rounded-full relative hover:bg-muted transition-colors"
                                data-testid="notifications-btn"
                                onClick={() => navigate('/notifications')}
                            >
                                <Bell className="w-5 h-5" />
                                <span className="absolute top-1 right-1 w-2 h-2 bg-destructive rounded-full" />
                            </Button>

                            {/* User menu */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="gap-2 rounded-full"
                                        data-testid="user-menu-btn"
                                    >
                                        <Avatar className="h-8 w-8 bg-primary">
                                            <AvatarFallback className="bg-primary text-white text-xs">
                                                {getInitials(user?.first_name, user?.last_name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="hidden md:inline-block text-sm font-medium">
                                            {user?.first_name}
                                        </span>
                                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuLabel>
                                        <div className="flex flex-col">
                                            <span>{user?.first_name} {user?.last_name}</span>
                                            <span className="text-xs font-normal text-muted-foreground">
                                                {user?.email}
                                            </span>
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                                        <UserCog className="w-4 h-4 mr-2" />
                                        Mon profil
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={handleLogout}
                                        className="text-destructive focus:text-destructive"
                                        data-testid="logout-btn"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Déconnexion
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="p-4 lg:p-8">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;
